import type { MatchOutcome, OverlayData, PanelRequest } from '@mogzo/game-ui';
import {
  type AchievementView,
  achievementParamsSchema,
  achievementProgressParamsSchema,
  BRIDGE_METHODS,
  type BridgeMethod,
  baseModeParamsSchema,
  type ChatPhrase,
  chatOpenParamsSchema,
  chatSayParamsSchema,
  createMatchParamsSchema,
  dataDocParamsSchema,
  dataQueryParamsSchema,
  dataSetParamsSchema,
  endMatchParamsSchema,
  findMatchParamsSchema,
  type GameEvent,
  type GameMessage,
  type GameSettings,
  type GameUiLook,
  type GameView,
  gameMessageSchema,
  joinMatchParamsSchema,
  type LaunchParams,
  launchRequestSchema,
  leaderboardViewParamsSchema,
  leaveMatchParamsSchema,
  type MatchNext,
  type MatchStart,
  type MatchView,
  moveParamsSchema,
  openMatchParamsSchema,
  type PageMessage,
  type RoomChoice,
  reportAttackParamsSchema,
  roomConnectionParamsSchema,
  roomTicketParamsSchema,
  saveBaseParamsSchema,
  saveKeyParamsSchema,
  saveSetParamsSchema,
  showLeaderboardParamsSchema,
  submitScoreParamsSchema,
  waitParamsSchema,
} from '@mogzo/shared';
import type { PlayApi } from './api';
import {
  baseCalls,
  deleteDocument,
  fetchPlayer,
  getDocument,
  getLeaderboard,
  getUiLook,
  listBoards,
  matchCalls,
  type PlayAuth,
  PlayError,
  type PlaySession,
  queryDocuments,
  setDocument,
} from './play';
import { createPlayCache } from './play-cache';

/** More than this many messages a second from one game are dropped. */
const MAX_MESSAGES_PER_SECOND = 50;
export interface BridgeOptions {
  /** The game's frame; only messages from its window count. */
  frame: () => HTMLIFrameElement | null;
  /** The game's exact origin (from its play URL), e.g. https://play.mogzogames.online. */
  gameOrigin: string;
  projectId: string;
  /** Starts the play session the bridge calls Game Services with. */
  api: PlayApi;
  /** The game's splash or the game itself (the default). */
  view?: GameView;
  /** For a launched game: what its splash launched it with. */
  launch?: LaunchParams;
  /** For a launched game: an invite code from a match link, for `multiplayer.join()` (M31). */
  invite?: string | null;
  /** For a launched game: a match to open, from a notification or email (M33). */
  openMatch?: string | null;
  /** For a splash: the player pressed its Play. */
  onLaunch?: (params: LaunchParams) => void;
  /** Called when the game says hello (it uses the SDK). */
  onConnected?: () => void;
  /**
   * Opens one of MOGZO's panels (M19); resolves when the player closes it, with what they did
   * for the multiplayer panel.
   */
  openPanel?: (panel: PanelRequest) => Promise<unknown>;
  /** Closes the room panel if it's open (the connection came back, M35), as if chosen `result`. */
  closePanel?: (result: unknown) => void;
  /** The player's choices in the pause menu, for the welcome. */
  settings?: () => GameSettings;
  /** An achievement the player unlocked just now, for the pop-up. */
  onUnlock?: (achievement: AchievementView) => void;
  /**
   * The match the game is playing now, as it starts, opens or moves in one (null once the
   * player leaves it): the page opens that match's chat (M38b).
   */
  onMatch?: (matchId: string | null) => void;
  /** The game's `mogzo.chat` calls (M38b); without it they answer that chat isn't here. */
  chat?: {
    open: (look?: 'panel' | 'compact') => void;
    close: () => void;
    say: (phrase: string) => Promise<void>;
  };
}

/** A connected bridge: what MOGZO's panels read with the game's play token. */
export interface GameBridge {
  disconnect: () => void;
  /** Sends saves and progress waiting to go (M24b), as when the game is paused. */
  flush: () => Promise<void>;
  data: OverlayData;
  look: () => Promise<GameUiLook>;
  /**
   * Tells the game a player in its match sent a quick phrase (M38b), naming them by their id in
   * this game, never their account.
   */
  phrase: (name: string, phrase: string, mine: boolean) => void;
}

/**
 * Tells the game in `frame` that it's paused or back (the SDK's onPause and onResume), or that
 * the player changed a setting (onSettings).
 */
export function sendGameEvent(
  frame: HTMLIFrameElement | null,
  gameOrigin: string,
  name: GameEvent,
  settings?: GameSettings,
  phrase?: ChatPhrase,
) {
  frame?.contentWindow?.postMessage(
    {
      mogzo: 1,
      type: 'event',
      name,
      ...(settings ? { settings } : {}),
      ...(phrase ? { phrase } : {}),
    },
    gameOrigin,
  );
}

/** Past every event a match can have: a turns call that answers with the match alone. */
const NO_EVENTS = 1_000_000_000;

/** What the game gets once its match starts. */
export function matchStartOf(view: MatchView): MatchStart {
  return {
    id: view.id,
    mode: view.mode.id,
    players: view.players
      .filter((p) => !p.left)
      .map((p) => ({ id: p.playerId, name: p.name, me: p.me, host: p.host })),
  };
}

/**
 * The page side of the SDK bridge (PRD 5.3), on mogzo.com or the admin site. It answers only its own frame, only at
 * the game's origin, only messages that match the schema, and keeps the play token to
 * itself: the game asks, the page calls the API.
 */
export function connectGameBridge({
  frame,
  gameOrigin,
  projectId,
  api,
  view = 'game',
  launch = {},
  invite = null,
  openMatch = null,
  onLaunch,
  onConnected,
  openPanel,
  closePanel,
  settings,
  onUnlock,
  onMatch,
  chat,
}: BridgeOptions): GameBridge {
  let session: Promise<PlaySession> | null = null;
  let windowStart = 0;
  let windowCount = 0;

  const ensureSession = () => {
    session ??= api.startSession(projectId).catch((error) => {
      session = null;
      throw error;
    });
    return session;
  };

  function reply(message: PageMessage) {
    frame()?.contentWindow?.postMessage(message, gameOrigin);
  }

  /** Calls the API with the play token; when the hour has run out, starts a new session once. */
  async function withToken<T>(call: (auth: PlayAuth) => Promise<T>): Promise<T> {
    const auth = (token: string) => ({ apiUrl: api.apiUrl, token });
    const { token } = await ensureSession();
    try {
      return await call(auth(token));
    } catch (error) {
      if (!(error instanceof PlayError) || error.status !== 401) throw error;
      session = null;
      return call(auth((await ensureSession()).token));
    }
  }

  const cache = createPlayCache(withToken, { onUnlock });
  const matchApi = matchCalls(withToken);
  const baseApi = baseCalls(withToken);
  /** The match link's code, until the game joins with it. */
  let pendingInvite = view === 'game' ? invite : null;

  function panel(request: PanelRequest): Promise<unknown> {
    if (!openPanel) {
      throw new PlayError(400, 'not_available', 'MOGZO’s panels aren’t available here.');
    }
    return openPanel(request);
  }

  /** Unlocks answer with `newlyUnlocked`; the page shows the pop-up then. */
  function announce<T extends { newlyUnlocked: boolean; achievement: AchievementView }>(
    result: T,
  ): T {
    if (result.newlyUnlocked) onUnlock?.(result.achievement);
    return result;
  }

  /** The game's arguments, checked; a mistake goes back to the game as `bad_request`. */
  function checked<T>(
    schema: {
      safeParse(
        value: unknown,
      ): { success: true; data: T } | { success: false; error: { issues: { message: string }[] } };
    },
    params: unknown,
  ): T {
    const result = schema.safeParse(params);
    if (result.success) return result.data;
    throw new PlayError(
      400,
      'bad_request',
      result.error.issues[0]?.message ?? 'Check the arguments.',
    );
  }

  /** Multiplayer needs an account: a guest is asked to sign in, and the game told why. */
  async function needPlayer() {
    const signedIn = await ensureSession()
      .then((s) => Boolean(s.player))
      .catch(() => false);
    if (signedIn) return;
    await panel({ kind: 'signin' });
    throw new PlayError(403, 'sign_in_needed', 'Players sign in to play with others.');
  }

  /** Opens the multiplayer panel; resolves once the match starts. */
  async function matchFrom(open: Extract<PanelRequest, { kind: 'match' }>['open']) {
    if (view !== 'game') {
      throw new PlayError(400, 'not_in_splash', 'Start multiplayer from the game, not its splash.');
    }
    await needPlayer();
    const outcome = (await panel({ kind: 'match', open })) as MatchOutcome | undefined;
    if (!outcome || outcome.action === 'cancel' || outcome.action === 'done') {
      throw new PlayError(400, 'cancelled', 'The player left without a match.');
    }
    return outcome;
  }

  /** The game is in this match now (or none): its chat follows (M38b). */
  let currentMatch: string | null = null;
  /** The match's players by name, with their ids in this game, for phrases. */
  let playerIds = new Map<string, string>();
  function inMatch(id: string | null) {
    if (id === currentMatch) return;
    currentMatch = id;
    onMatch?.(id);
  }
  function knowPlayers(start: MatchStart) {
    playerIds = new Map(start.players.map((p) => [p.name, p.id]));
    return start;
  }

  /** A started match for the game, with its turn and state (turns modes, M32). */
  async function started(view: MatchView): Promise<MatchStart> {
    inMatch(view.id);
    knowPlayers(matchStartOf(view));
    return matchApi
      .turns(view.id, NO_EVENTS)
      .then((update) => update.match)
      .catch(() => matchStartOf(view));
  }

  async function answer(method: BridgeMethod, params: unknown): Promise<unknown> {
    switch (method) {
      case 'multiplayer.find': {
        const { mode } = checked(findMatchParamsSchema, params);
        return started((await matchFrom({ how: 'quick', mode })).match);
      }
      case 'multiplayer.create': {
        const { mode } = checked(createMatchParamsSchema, params);
        return started((await matchFrom({ how: 'invite', mode })).match);
      }
      case 'multiplayer.join': {
        const { code } = checked(joinMatchParamsSchema, params ?? {});
        const use = code ?? pendingInvite ?? undefined;
        pendingInvite = null;
        return started((await matchFrom({ how: 'join', code: use })).match);
      }
      case 'matches.mine':
        return matchApi.mine();
      case 'matches.open': {
        const { match } = checked(openMatchParamsSchema, params);
        const opened = (await matchApi.turns(match, NO_EVENTS)).match;
        inMatch(match);
        return knowPlayers(opened);
      }
      case 'matches.move': {
        const { match, ...move } = checked(moveParamsSchema, params);
        inMatch(match);
        return matchApi.move(match, move);
      }
      case 'matches.wait': {
        const { match, after, now } = checked(waitParamsSchema, params);
        return matchApi.turns(match, after, !now);
      }
      case 'multiplayer.end': {
        const { match, result } = checked(endMatchParamsSchema, params);
        const ended = await matchApi.results(match, result);
        const outcome = (await panel({ kind: 'match', open: { how: 'results', match: ended } })) as
          | MatchOutcome
          | undefined;
        const next: MatchNext =
          outcome && (outcome.action === 'rematch' || outcome.action === 'new')
            ? { action: outcome.action, match: await started(outcome.match) }
            : { action: 'done' };
        return next;
      }
      case 'multiplayer.leave': {
        const { match } = checked(leaveMatchParamsSchema, params);
        await matchApi.leave(match);
        if (match === currentMatch) inMatch(null);
        return null;
      }
      case 'rooms.ticket': {
        const { match } = checked(roomTicketParamsSchema, params);
        inMatch(match);
        // A new room past a realtime limit: MOGZO's "busy" panel, to try again or give up.
        for (;;) {
          try {
            return await matchApi.room(match);
          } catch (error) {
            if (!(error instanceof PlayError) || error.code !== 'multiplayer_busy') throw error;
            const choice = (await panel({ kind: 'room', show: 'busy' })) as RoomChoice | undefined;
            if (choice?.action !== 'find') {
              throw new PlayError(409, 'multiplayer_busy', 'Multiplayer is busy. Try again later.');
            }
          }
        }
      }
      case 'rooms.connection': {
        const { state, until } = checked(roomConnectionParamsSchema, params);
        if (state === 'back') {
          closePanel?.({ action: 'back' } satisfies RoomChoice);
          return null;
        }
        const request: PanelRequest =
          state === 'lost'
            ? { kind: 'room', show: 'reconnecting', until: until ?? Date.now() + 30_000 }
            : { kind: 'room', show: 'lost' };
        return ((await panel(request)) as RoomChoice | undefined) ?? { action: 'done' };
      }
      case 'bases.save': {
        const { mode, base } = checked(saveBaseParamsSchema, params);
        await needPlayer();
        return baseApi.save(mode, base);
      }
      case 'bases.mine': {
        const { mode } = checked(baseModeParamsSchema, params);
        return baseApi.mine(mode);
      }
      case 'bases.findOpponent': {
        const { mode } = checked(baseModeParamsSchema, params);
        await needPlayer();
        return baseApi.findOpponent(mode);
      }
      case 'bases.reportAttack': {
        const { attack, result } = checked(reportAttackParamsSchema, params);
        return baseApi.report(attack, result);
      }
      case 'bases.inbox': {
        const { mode } = checked(baseModeParamsSchema, params);
        return baseApi.inbox(mode);
      }
      case 'player.me':
        return withToken(fetchPlayer);
      case 'post.context':
        return null;
      case 'save.get': {
        const { key } = checked(saveKeyParamsSchema, params);
        return cache.getSave(key);
      }
      case 'save.set': {
        const { key, value } = checked(saveSetParamsSchema, params);
        await cache.setSave(key, value);
        return null;
      }
      case 'save.delete': {
        const { key } = checked(saveKeyParamsSchema, params);
        return cache.deleteSave(key);
      }
      case 'save.list':
        return cache.listSaves();
      case 'data.get': {
        const { collection, scope, id } = checked(dataDocParamsSchema, params);
        return withToken((auth) => getDocument(auth, collection, scope, id));
      }
      case 'data.set': {
        const { collection, scope, id, value } = checked(dataSetParamsSchema, params);
        return withToken((auth) => setDocument(auth, collection, scope, id, value));
      }
      case 'data.delete': {
        const { collection, scope, id } = checked(dataDocParamsSchema, params);
        return withToken((auth) => deleteDocument(auth, collection, scope, id));
      }
      case 'data.query': {
        const { collection, scope, query } = checked(dataQueryParamsSchema, params);
        return withToken((auth) => queryDocuments(auth, collection, scope, query));
      }
      case 'config.get':
        return cache.getConfig();
      case 'events.active':
        return cache.getEvents();
      case 'leaderboards.submit': {
        const { id, score } = checked(submitScoreParamsSchema, params);
        return cache.submitScore(id, score);
      }
      case 'leaderboards.top':
      case 'leaderboards.aroundMe': {
        const { id, period, limit } = checked(leaderboardViewParamsSchema, params);
        const view = method === 'leaderboards.top' ? 'top' : 'around';
        return withToken((auth) => getLeaderboard(auth, id, view, period, limit));
      }
      case 'achievements.unlock': {
        const { id } = checked(achievementParamsSchema, params);
        return announce(await cache.unlock(id));
      }
      case 'achievements.progress': {
        const { id, value } = checked(achievementProgressParamsSchema, params);
        return announce(await cache.progress(id, value));
      }
      case 'achievements.list':
        return cache.listAchievements();
      case 'launch': {
        if (view !== 'splash' || !onLaunch) {
          throw new PlayError(400, 'not_a_splash', 'Only the game’s splash can launch the game.');
        }
        // A tap or click in the splash counts for this page too; the start of a swipe through
        // the feed doesn't. Browsers that can't tell (older ones) launch anyway.
        if (navigator.userActivation && !navigator.userActivation.isActive) {
          throw new PlayError(
            400,
            'no_tap',
            'Launch the game from a tap or click, such as on a Play button.',
          );
        }
        const request = checked(launchRequestSchema, params ?? {});
        onLaunch(request.params);
        return null;
      }
      case 'ui.showLeaderboard': {
        const options = checked(showLeaderboardParamsSchema, params ?? {});
        await panel({ kind: 'leaderboard', ...options });
        return null;
      }
      case 'ui.showAchievements':
        await panel({ kind: 'achievements' });
        return null;
      case 'ui.showPauseMenu':
        void cache.flush();
        await panel({ kind: 'pause' });
        return null;
      case 'chat.open': {
        const { look } = checked(chatOpenParamsSchema, params ?? {});
        if (!chat || !currentMatch) {
          throw new PlayError(409, 'no_match_chat', 'Chat opens once the player is in a match.');
        }
        chat.open(look);
        return null;
      }
      case 'chat.close':
        chat?.close();
        return null;
      case 'chat.say': {
        const { phrase } = checked(chatSayParamsSchema, params);
        if (!chat || !currentMatch) {
          throw new PlayError(409, 'no_match_chat', 'Chat opens once the player is in a match.');
        }
        await chat.say(phrase);
        return null;
      }
      case 'ui.promptSignIn': {
        // Only guests are asked; a signed-in player has a session with a player.
        const signedIn = await ensureSession()
          .then((s) => Boolean(s.player))
          .catch(() => false);
        if (!signedIn) await panel({ kind: 'signin' });
        return null;
      }
    }
  }

  async function handle(message: GameMessage) {
    if (message.type === 'hello') {
      onConnected?.();
      const welcomeInvite = pendingInvite;
      const player = await ensureSession()
        .then((s) => s.player)
        .catch(() => null);
      reply({
        mogzo: 1,
        type: 'welcome',
        id: message.id,
        player,
        post: null,
        view,
        launch: view === 'game' ? launch : {},
        ...(settings ? { settings: settings() } : {}),
        invite: welcomeInvite,
        openMatch: view === 'game' ? openMatch : null,
      });
      return;
    }
    if (!(BRIDGE_METHODS as readonly string[]).includes(message.method)) {
      reply({
        mogzo: 1,
        type: 'error',
        id: message.id,
        code: 'unknown_method',
        message: `${message.method} isn’t available in this version of MOGZO.`,
      });
      return;
    }
    try {
      reply({
        mogzo: 1,
        type: 'response',
        id: message.id,
        result: await answer(message.method as BridgeMethod, message.params),
      });
    } catch (error) {
      // The game hears the API's reason for its own mistakes (a save too big, too many
      // calls); anything else is a plain "try again".
      const told =
        error instanceof PlayError && error.status < 500 && error.status !== 401
          ? { code: error.code, message: error.message }
          : { code: 'unavailable', message: 'MOGZO couldn’t answer just now. Try again.' };
      reply({ mogzo: 1, type: 'error', id: message.id, ...told });
    }
  }

  function onMessage(event: MessageEvent) {
    const source = frame()?.contentWindow;
    if (!source || event.source !== source || event.origin !== gameOrigin) return;
    const now = Date.now();
    if (now - windowStart > 1000) {
      windowStart = now;
      windowCount = 0;
    }
    if (++windowCount > MAX_MESSAGES_PER_SECOND) return;
    const parsed = gameMessageSchema.safeParse(event.data);
    if (parsed.success) void handle(parsed.data);
  }

  // The page may be gone soon after it's hidden (a phone switching apps), or closing.
  const onHidden = () => {
    if (document.visibilityState === 'hidden') void cache.flush({ keepalive: true });
  };
  const onPageHide = () => void cache.flush({ keepalive: true });

  window.addEventListener('message', onMessage);
  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', onPageHide);
  return {
    disconnect: () => {
      window.removeEventListener('message', onMessage);
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', onPageHide);
      void cache.flush({ keepalive: true });
    },
    flush: () => cache.flush(),
    data: {
      boards: () => withToken(listBoards),
      leaderboard: (id, boardView, period, limit) =>
        withToken((auth) => getLeaderboard(auth, id, boardView, period, limit)),
      achievements: () => cache.listAchievements(),
      multiplayer: matchApi,
    },
    look: () => withToken(getUiLook),
    phrase: (name, phrase, mine) =>
      sendGameEvent(frame(), gameOrigin, 'phrase', undefined, {
        player: { id: playerIds.get(name) ?? '', name },
        phrase,
        mine,
      }),
  };
}
